import type { MetaFunction } from "@remix-run/cloudflare";
import { useState } from "react";
import { Link } from "@remix-run/react";
import Footer from "~/components/Footer";
import AppAuthGate, { isAppAuthenticated } from "~/components/AppAuthGate";
import { SplashScreen } from "~/components/SplashScreen";
import { useScrollReveal } from "~/lib/use-scroll-reveal";
import styles from "~/styles/skills.module.css";

export const meta: MetaFunction = () => [
  { title: "Skillarq — Prodigium Dynamics" },
  { name: "description", content: "Engineered system prompts packaged as .MD files. Built in production, not theory." },
];

type Filter = "all" | "design" | "data" | "training";

const skills = [
  { id: "data-analyst-pro", category: "data" as Filter, tag: "Data Analysis", name: "DATA ANALYST\nPRO", desc: "Turn any AI into a structured analyst. Load a dataset — CSV, table, report — and get findings you can actually act on. No assumptions. No vague summaries.", features: ["Asks for context before touching the data", "Identifies outliers, patterns, and gaps — not just averages", "Flags what the data can't answer", "No code execution required"], compat: ["Claude", "ChatGPT", "Gemini", "Any LLM"], tags: ["Business Intelligence", "CSV / Tables", "Reports", "Decision Support"], price: "$10", featured: true, status: "available" },
  { id: "landing-designer", category: "design" as Filter, tag: "Design", name: "LANDING &\nPORTFOLIO\nDESIGNER", desc: "UI direction for landing pages and portfolios. Interviews you on brand and goals, then delivers structured specs a developer can build from directly.", features: [], compat: ["Claude", "ChatGPT", "Any LLM"], tags: ["Landing Pages", "Portfolios", "UI Direction", "Copy Structure"], price: "$10", featured: false, status: "available" },
  { id: "ai-onboarding-kit", category: "training" as Filter, tag: "Training · Free", name: "AI ONBOARDING\nKIT", desc: "The entry point. Teaches any AI to ask before it acts and deliver output that's actually ready to use. Free, no email wall.", features: [], compat: ["Claude", "ChatGPT", "Gemini", "Any LLM"], tags: ["Beginner Friendly", "Any AI", "Workflow Foundation"], price: "Free", featured: false, status: "free" },
  { id: "brand-identity", category: "design" as Filter, tag: "Design", name: "BRAND\nIDENTITY\nSYSTEM", desc: "A complete brand interrogation and output system. Voice, visual language, naming conventions, and copy guidelines — delivered as a structured brief.", features: [], compat: [], tags: [], price: "$10", featured: false, status: "coming" },
];

export default function SkillarqPage() {
  const [authReady, setAuthReady] = useState(() => isAppAuthenticated("SKILLARQ"));

  const [filter, setFilter] = useState<Filter>("all");

  useScrollReveal([filter]);

  const visible = skills.filter((s) => filter === "all" || s.category === filter);
  const featuredSkill = visible.find((s) => s.featured);
  const gridItems = featuredSkill ? visible.filter((s) => !s.featured) : visible;

  if (!authReady) {
    return <AppAuthGate appName="SKILLARQ" appTag="System Prompts" onAuth={() => setAuthReady(true)} />;
  }

  return (
    <>
      <SplashScreen name="SKILLARQ" tag="System Prompts" />

      <div className={styles.pageCoverHidden} />

      <section className={styles.hero} style={{ paddingTop: 120 }}>
        <p className={styles.heroEyebrow}>Skillarq — System Prompts</p>
        <h1 className={styles.heroTitle}>ENGINEERED<br />FOR <span>RESULTS.</span></h1>
        <p className={styles.heroDesc}>
          Not theory. Not tutorials. These are the exact systems used to build real products under
          Prodigium Dynamics — packaged as .MD files, ready to load into any AI.
        </p>
        <div className={styles.heroActions}>
          <a href="#catalog" className="btn-primary">Browse Catalog</a>
          <a href="#how" className="btn-ghost">How it works</a>
        </div>
      </section>

      <div className={styles.tickerWrap}>
        <div className={styles.tickerTrack}>
          {Array.from({ length: 2 }).flatMap(() =>
            ["Skillarq", "✦", "System Prompts", "✦", "Built in Production", "✦", "Not Theory", "✦", "Works on Any AI", "✦", "Claude · ChatGPT · Gemini", "✦", ".MD Format", "✦", "2 Years of Updates", "✦"].map((t, i) => (
              <span key={`${t}${i}`} className={styles.tickerItem}>{t}</span>
            ))
          )}
        </div>
      </div>

      <div className="rule" />

      <section id="how" className={styles.howSection}>
        <p className={`${styles.sectionLabel} reveal`}>How it works</p>
        <div className={`${styles.howGrid} reveal`}>
          {[
            { n: "01", title: "Download the file", text: "You get a single .MD file. Clean, readable, structured. No subscriptions, no dashboards, no onboarding flows." },
            { n: "02", title: "Load it into your AI", text: "Paste the content into your system prompt — Claude, ChatGPT, Gemini, or any other model. The skill takes over from there." },
            { n: "03", title: "Get consistent output", text: "The AI stops guessing and starts executing. Every session. Every model. Predictable quality that compounds over time." },
          ].map(({ n, title, text }) => (
            <div key={n} className={styles.howStep}>
              <div className={styles.howNumber}>{n}</div>
              <p className={styles.howTitle}>{title}</p>
              <p className={styles.howText}>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <div className={styles.manifesto}>
        <p className="reveal">
          Most people use AI like a search engine. They type. They hope. They edit.{" "}
          <strong>Skillarq changes the operating model.</strong>{" "}
          You&apos;re not prompting anymore — you&apos;re deploying a system.
        </p>
      </div>

      <section id="catalog" className={styles.catalogSection}>
        <p className={`${styles.sectionLabel} reveal`}>Catalog</p>

        <div className={`${styles.freeHighlight} reveal`}>
          <span className={styles.freeBadge}>Free Skill</span>
          <p className={styles.freeText}>
            Every catalog includes one free skill — no email required. Download it, use it, share it.{" "}
            <a href="https://github.com/prodigiumdynamics" target="_blank" rel="noopener">Also on GitHub ↗</a>
          </p>
        </div>

        <div className={`${styles.filterBar} reveal`}>
          {(["all", "design", "data", "training"] as Filter[]).map((f) => (
            <button key={f} className={`${styles.filterBtn} ${filter === f ? styles.filterBtnActive : ""}`} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {featuredSkill && (
          <div className={`${styles.skillCard} ${styles.skillCardFeatured} reveal`}>
            <div>
              <div className={styles.skillImage}>
                <div className={styles.skillImagePlaceholder}>{featuredSkill.name.replace("\n", " ")} — preview</div>
              </div>
              <p className={styles.skillCategory}>{featuredSkill.tag}</p>
              <h2 className={styles.skillName}>{featuredSkill.name.split("\n").map((l, i) => <span key={i}>{l}<br /></span>)}</h2>
              <p className={styles.skillDesc}>{featuredSkill.desc}</p>
              {featuredSkill.features.length > 0 && (
                <ul className={styles.skillWhatList}>{featuredSkill.features.map((f) => <li key={f}>{f}</li>)}</ul>
              )}
              <div className={styles.compatRow}>
                <span className={styles.compatLabel}>Works with</span>
                <div className={styles.compatBadges}>{featuredSkill.compat.map((c) => <span key={c} className={styles.compatBadge}>{c}</span>)}</div>
              </div>
            </div>
            <div className={styles.skillFeaturedRight}>
              <div className={styles.skillTags}>{featuredSkill.tags.map((t) => <span key={t} className={styles.skillTag}>{t}</span>)}</div>
              <div className={styles.skillFooter}>
                <div>
                  <div className={styles.skillPrice}>{featuredSkill.price} <span>USD</span></div>
                  <div style={{ marginTop: 8 }}><span className={styles.updatesChip}>2 years of updates included</span></div>
                </div>
                <a href="#" className={styles.btnSkill}>Get via Whop →</a>
              </div>
            </div>
          </div>
        )}

        <div className={styles.skillsGrid}>
          {gridItems.map((skill) => (
            <div key={skill.id} className={`${styles.skillCard} reveal ${skill.status === "free" ? styles.skillCardFree : ""} ${skill.status === "coming" ? styles.skillCardComing : ""}`}>
              <div className={styles.skillImage}>
                <div className={styles.skillImagePlaceholder}>{skill.name.replace("\n", " ")} — preview</div>
              </div>
              <p className={styles.skillCategory}>{skill.tag}</p>
              <h2 className={styles.skillName}>{skill.name.split("\n").map((l, i) => <span key={i}>{l}<br /></span>)}</h2>
              <p className={styles.skillDesc}>{skill.desc}</p>
              {skill.tags.length > 0 && <div className={styles.skillTags}>{skill.tags.map((t) => <span key={t} className={styles.skillTag}>{t}</span>)}</div>}
              {skill.compat.length > 0 && (
                <div className={styles.compatRow}>
                  <span className={styles.compatLabel}>Works with</span>
                  <div className={styles.compatBadges}>{skill.compat.map((c) => <span key={c} className={styles.compatBadge}>{c}</span>)}</div>
                </div>
              )}
              <div className={styles.skillFooter}>
                <div>
                  <div className={`${styles.skillPrice} ${skill.status === "free" ? styles.skillPriceFree : ""} ${skill.status === "coming" ? styles.skillPriceComingSoon : ""}`}>
                    {skill.price} {skill.price !== "Free" && skill.status !== "coming" && <span>USD</span>}
                  </div>
                  {skill.status === "available" && <div style={{ marginTop: 8 }}><span className={styles.updatesChip}>2 years of updates included</span></div>}
                </div>
                {skill.status === "available" && <a href="#" className={styles.btnSkill}>Get via Whop →</a>}
                {skill.status === "free" && <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}><a href="#" className={styles.btnSkill}>Download .MD</a><a href="https://github.com/prodigiumdynamics" target="_blank" rel="noopener" className={`${styles.btnSkill} ${styles.btnSkillGhost}`}>GitHub ↗</a></div>}
                {skill.status === "coming" && <button className={`${styles.btnSkill} ${styles.btnSkillDisabled}`} disabled>Coming Soon</button>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="rule" />

      <section className={styles.ctaSection}>
        <div className="reveal">
          <p className={styles.ctaEyebrow}>More Skills In Development</p>
          <h2 className={styles.ctaTitle}>The catalog grows with production use.</h2>
          <p className={styles.ctaDesc}>
            Every skill in this catalog was built while shipping a real product.
            Nothing is speculative. New skills are added when they&apos;ve been proven in production — not before.
          </p>
          <Link to="/contact" className="btn-primary">Request a Custom Skill</Link>
        </div>
      </section>

      <div className="rule" />
      <Footer />
    </>
  );
}
